// src/components/Footer.js
import React, { useState, useEffect } from 'react';
import { useSchoolName } from './schoolNameStore';

export default function Footer() {
  const schoolName = useSchoolName();
  const [now, setNow] = useState(new Date());
  const [isClient, setIsClient] = useState(false);
  const [sealDrawn, setSealDrawn] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const raf = requestAnimationFrame(() => setSealDrawn(true));
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(timer);
    };
  }, []);

  if (!isClient) return null;

  const dayLabel = now.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  });

  const timeLabel = now.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });

  return (
    <footer className="classora-footer relative mt-10 overflow-hidden rounded-none border-t border-[#90CAF9]/30 bg-[radial-gradient(ellipse_at_top,_#2196F3_0%,_#0D47A1_55%,_#092F6E_100%)] shadow-[0_-20px_60px_-15px_rgba(13,71,161,0.7)]">

        {/* faint engraved texture lines */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" style={{
          backgroundImage: 'repeating-linear-gradient(115deg, #E3F2FD 0px, #E3F2FD 1px, transparent 1px, transparent 14px)'
        }}></div>

        {/* soft gold atmosphere */}
        <div className="pointer-events-none absolute -top-24 left-1/4 w-72 h-72 bg-[#90CAF9]/10 rounded-full blur-[90px]"></div>
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-80 h-80 bg-[#90CAF9]/10 rounded-full blur-[100px]"></div>

        {/* hairline gold rule at very top, drawn on load */}
        <div className="absolute top-0 left-0 right-0 h-px bg-[#90CAF9]/40 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-transparent via-[#90CAF9] to-transparent transition-transform duration-[1400ms] ease-out"
            style={{ transform: sealDrawn ? 'translateX(0%)' : 'translateX(-110%)' }}
          ></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-10 pb-8">

          {/* ============ MAIN ROW: Crest + Name + Address | Motto ============ */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] items-center gap-8 md:gap-10">

            {/* --- Crest / Wordmark / Address --- */}
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <div
                className="relative w-16 h-16 shrink-0 rounded-full bg-white/95 border border-[#90CAF9]/60 shadow-[0_4px_12px_rgba(33,150,243,0.35)] flex items-center justify-center overflow-hidden p-1.5 transition-transform duration-700"
                style={{ transform: sealDrawn ? 'scale(1)' : 'scale(0.85)', opacity: sealDrawn ? 1 : 0 }}
              >
                <img src="/school-logo.png" alt="School Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col">
                <span className="font-[Playfair_Display,Georgia,serif] text-2xl font-bold tracking-wide text-[#E3F2FD]">
                  {schoolName}
                </span>
                <span className="mt-0.5 font-[IBM_Plex_Mono,monospace] text-[10px] tracking-[0.25em] uppercase text-[#90CAF9]/80">
                  Smart Campus &middot; Est. Excellence
                </span>
                <span className="mt-1.5 font-[IBM_Plex_Mono,monospace] text-[10px] tracking-wide text-[#E3F2FD]/70">
                  <i className="fas fa-map-marker-alt mr-1.5 text-[#90CAF9]"></i>
                  School Address Line, City, State &ndash; PIN Code
                </span>
              </div>
            </div>

            {/* --- Center motto with drawing underline --- */}
            <div className="flex flex-col items-center text-center">
              <p className="font-[Playfair_Display,Georgia,serif] italic text-[15px] text-[#E3F2FD] leading-snug">
                &ldquo;Where Knowledge Meets Innovation&rdquo;
              </p>
              <div className="relative mt-2 h-px w-40 bg-[#90CAF9]/20 overflow-hidden">
                <div
                  className="h-full bg-[#90CAF9] transition-transform duration-[1200ms] ease-out"
                  style={{ transform: sealDrawn ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'center' }}
                ></div>
              </div>
              <p className="mt-2 font-[IBM_Plex_Mono,monospace] text-[9px] tracking-[0.2em] uppercase text-[#90CAF9]">
                Affiliated Secondary &amp; Higher Secondary School
              </p>
            </div>
          </div>

          {/* ============ TICKER STRIP: Date & Time ============ */}
          <div className="mt-8 flex items-center gap-3">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#90CAF9]/30 to-[#90CAF9]/30"></span>
            <div className="flex items-center gap-4 px-4 py-1.5 rounded-full border border-[#90CAF9]/25 bg-[#0D47A1]/60 backdrop-blur-sm">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#90CAF9] animate-pulse"></span>
                <span className="font-[IBM_Plex_Mono,monospace] text-[9px] tracking-widest uppercase text-[#90CAF9]">Campus Live</span>
              </span>
              <span className="w-px h-3 bg-[#90CAF9]/25"></span>
              <span className="font-[IBM_Plex_Mono,monospace] text-[9px] tracking-wide text-[#E3F2FD]/80">{dayLabel}</span>
              <span className="w-px h-3 bg-[#90CAF9]/25"></span>
              <span className="font-[IBM_Plex_Mono,monospace] text-[9px] tracking-wide text-[#E3F2FD]/80 tabular-nums">{timeLabel} IST</span>
            </div>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent via-[#90CAF9]/30 to-[#90CAF9]/30"></span>
          </div>
        </div>

        <style jsx>{`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
          .classora-footer { font-feature-settings: 'tnum' 1; }
          @media (prefers-reduced-motion: reduce) {
            .classora-footer * { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
          }
        `}</style>
      </footer>
  );
}